<template>
  <div v-if="show" class="modal-overlay" @click.self="handleClose">
    <div class="modal-content">
      <div class="modal-header">
        <h2>Modifier le bloc</h2>
        <button class="modal-close" @click="handleClose">×</button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <div class="form-group">
          <label for="object_name">Nom de l'objet *</label>
          <input
            id="object_name"
            v-model="formData.object_name"
            type="text"
            required
            placeholder="Nom du bloc"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="inventory_number">Numéro d'inventaire</label>
          <input
            id="inventory_number"
            v-model="formData.inventory_number"
            type="text"
            placeholder="Numéro d'inventaire"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="fragment_emplacement">Emplacement (conservation)</label>
          <textarea
            id="fragment_emplacement"
            v-model="formData.fragment_emplacement"
            placeholder="Ex. dépôt lapidaire…"
            rows="2"
            class="form-textarea"
          />
        </div>

        <div class="form-group">
          <label for="fragment_identification">Identification sommaire</label>
          <textarea
            id="fragment_identification"
            v-model="formData.fragment_identification"
            placeholder="Nature du fragment…"
            rows="2"
            class="form-textarea"
          />
        </div>

        <div class="form-group">
          <label for="poids_estime_kg">Poids estimé (kg)</label>
          <input
            id="poids_estime_kg"
            v-model.number="formData.poids_estime_kg"
            type="number"
            step="0.01"
            min="0"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="description">Description (texte libre / récap)</label>
          <textarea
            id="description"
            v-model="formData.description"
            placeholder="Description du bloc..."
            rows="3"
            class="form-textarea"
          />
        </div>

        <div class="form-group">
          <label for="remarks">Remarques</label>
          <textarea
            id="remarks"
            v-model="formData.remarks"
            placeholder="Remarques..."
            rows="2"
            class="form-textarea"
          />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="real_size_x_mm">Taille réelle X (mm)</label>
            <input
              id="real_size_x_mm"
              v-model.number="formData.real_size_x_mm"
              type="number"
              step="0.001"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label for="real_size_y_mm">Taille réelle Y (mm)</label>
            <input
              id="real_size_y_mm"
              v-model.number="formData.real_size_y_mm"
              type="number"
              step="0.001"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label for="real_size_z_mm">Taille réelle Z (mm)</label>
            <input
              id="real_size_z_mm"
              v-model.number="formData.real_size_z_mm"
              type="number"
              step="0.001"
              class="form-input"
            />
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" @click="handleClose" class="btn-cancel">
            Annuler
          </button>
          <button type="submit" :disabled="!formData.object_name.trim() || loading" class="btn-submit">
            {{ loading ? 'Enregistrement...' : 'Enregistrer' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useBlocsStore } from '@stores/blocs.store';
import { useToast } from '../composables/useToast';
import type { Bloc } from '@api/blocs.api';

const props = defineProps<{
  show: boolean;
  bloc: Bloc | null;
}>();

const emit = defineEmits<{
  close: [];
  updated: [bloc: Bloc];
}>();

const blocsStore = useBlocsStore();
const { success, error: showError } = useToast();

const formData = ref({
  object_name: '',
  inventory_number: '',
  fragment_emplacement: '',
  fragment_identification: '',
  poids_estime_kg: null as number | null,
  description: '',
  remarks: '',
  real_size_x_mm: null as number | null,
  real_size_y_mm: null as number | null,
  real_size_z_mm: null as number | null,
});

const loading = ref(false);

watch(
  () => props.bloc,
  (bloc) => {
    if (bloc) {
      formData.value = {
        object_name: bloc.object_name,
        inventory_number: bloc.inventory_number || '',
        fragment_emplacement: bloc.fragment_emplacement || '',
        fragment_identification: bloc.fragment_identification || '',
        poids_estime_kg: bloc.poids_estime_kg,
        description: bloc.description || '',
        remarks: bloc.remarks || '',
        real_size_x_mm: bloc.real_size_x_mm,
        real_size_y_mm: bloc.real_size_y_mm,
        real_size_z_mm: bloc.real_size_z_mm,
      };
    }
  },
  { immediate: true }
);

watch(
  () => props.show,
  (show) => {
    if (!show) {
      formData.value = {
        object_name: '',
        inventory_number: '',
        fragment_emplacement: '',
        fragment_identification: '',
        poids_estime_kg: null,
        description: '',
        remarks: '',
        real_size_x_mm: null,
        real_size_y_mm: null,
        real_size_z_mm: null,
      };
    }
  }
);

const handleClose = () => {
  emit('close');
};

const handleSubmit = async () => {
  if (!props.bloc || !formData.value.object_name.trim()) return;

  loading.value = true;
  try {
    const pe = formData.value.poids_estime_kg;
    const poids =
      pe === null || pe === undefined || !Number.isFinite(Number(pe)) ? null : Number(pe);

    const updated = await blocsStore.update(props.bloc.id, {
      object_name: formData.value.object_name,
      inventory_number: formData.value.inventory_number || undefined,
      fragment_emplacement: formData.value.fragment_emplacement.trim() || null,
      fragment_identification: formData.value.fragment_identification.trim() || null,
      poids_estime_kg: poids,
      description: formData.value.description || undefined,
      remarks: formData.value.remarks || undefined,
      real_size_x_mm: formData.value.real_size_x_mm ?? undefined,
      real_size_y_mm: formData.value.real_size_y_mm ?? undefined,
      real_size_z_mm: formData.value.real_size_z_mm ?? undefined,
    });
    success('Bloc modifié avec succès');
    emit('updated', updated);
    handleClose();
  } catch (err: any) {
    showError(err.message || 'Erreur lors de la modification');
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #444;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #e0e0e0;
}

.modal-close {
  background: none;
  border: none;
  color: #999;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #e0e0e0;
}

.modal-body {
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.9rem;
  font-weight: 500;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 0.75rem;
  background-color: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 0.9rem;
  font-family: inherit;
  transition: border-color 0.2s;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #4a9eff;
}

.form-textarea {
  resize: vertical;
  min-height: 60px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #444;
}

.btn-cancel,
.btn-submit {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-cancel {
  background-color: #444;
  color: #e0e0e0;
}

.btn-cancel:hover {
  background-color: #555;
}

.btn-submit {
  background-color: #4a9eff;
  color: white;
}

.btn-submit:hover:not(:disabled) {
  background-color: #3a8eef;
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
